import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileDown, Sparkles, Video, Palette, BookOpen, Feather, Users, ExternalLink, Languages, PlayCircle } from "lucide-react";
import { api, API, COLORS } from "@/lib/api";

const VIDEO_URL = "/concurso/concurso-video.mp4";
const FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSdEXTAkIP4fEQkm8cEpChS4WEOlqZI1I_mOVIcH1iIu6Z_gzA/viewform?embedded=true";
const PDF = "/concurso/convocatoria-2026.pdf";

const CATS = [
  { icon: Video, t: "Video corto", c: COLORS.purple },
  { icon: Palette, t: "Ilustración digital", c: "#00B4D8" },
  { icon: BookOpen, t: "Cuento", c: COLORS.magenta },
  { icon: Feather, t: "Poesía", c: COLORS.purple },
  { icon: Users, t: "Acción colectiva en el espacio público", c: COLORS.magenta },
];

const fade = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

const LENGUAS = [
  { nombre: "Rarámuri", url: "https://forms.gle/CoTBuhTLL99TSHWMA" },
  { nombre: "Maya", url: "https://forms.gle/6cgR12N6vxT8qk896" },
  { nombre: "Yaqui", url: "https://forms.gle/HATHk9zC62FvPTKj7" },
  { nombre: "Zapoteco", url: "https://forms.gle/QLukrkwaJLfL48L57" },
];

const TejiendoRedes = () => {
  const [talleres, setTalleres] = useState([]);
  useEffect(() => {
    api.get("/talleres").then((r) => setTalleres(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="subpage-tejiendo-redes-ciudadanas">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]" style={{ background: `linear-gradient(120deg, ${COLORS.orange} 0%, ${COLORS.magenta} 100%)` }}>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/programas" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Programas
          </Link>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-white/90 bg-white/20 rounded-full px-3 py-1 mb-5">5ta edición · Concurso Nacional</span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Tejiendo Redes Ciudadanas por Juventudes Diversas
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 font-medium max-w-2xl">
            Juventudes Latentes se suma como aliada estratégica del Instituto Nacional Electoral (INE) a
            esta 5ta edición del Concurso Nacional, en el marco de la ENCÍVICA 2024-2026.
          </p>
        </div>
      </section>

      {/* Banner 1 */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-12">
        <img src="/concurso/banner-1.webp" alt="5ta edición Concurso Nacional — Tejiendo Redes Ciudadanas por Juventudes Diversas" className="w-full h-auto rounded-2xl border border-[#E5E5E5]" />
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        {/* Sobre el concurso */}
        <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">¿Por qué este concurso?</h2>
          <p className="mt-4 text-brand-ink/80 leading-relaxed">
            Forma parte del esfuerzo de la Estrategia Nacional de Educación Cívica 2024-2026 (ENCÍVICA)
            para promover la participación de las juventudes diversas del país en torno al ejercicio de sus
            derechos políticos y electorales, visibilizando y reconociendo las barreras que dificultan su
            acceso en un contexto de discriminación histórica.
          </p>
        </motion.section>

        {/* En qué consiste */}
        <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="mt-10 rounded-2xl p-6 md:p-8" style={{ background: "#FDE7FA" }} data-testid="concurso-descripcion">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-brand-ink">¿En qué consiste?</h2>
          <p className="mt-4 text-brand-ink/80 leading-relaxed">
            Es una invitación a las juventudes a expresar, a través de la creatividad, sus ideas y
            experiencias sobre la participación ciudadana y sus derechos políticos y electorales:
            cómo influir en su comunidad, cómo combatir la discriminación y los discursos de odio, o
            cómo fue su primera vez votando. Tú eliges la categoría y el formato para contar tu historia.
          </p>
          <p className="mt-3 text-brand-ink/80 leading-relaxed">
            <span className="font-semibold text-brand-ink">¿Quién puede participar?</span> Todas las personas
            jóvenes de entre <span className="font-semibold text-brand-ink">18 y 29 años</span> cumplidos al 15 de
            octubre de 2026, residentes en cualquiera de las 32 entidades del país. Consulta las bases completas
            para conocer todos los requisitos.
          </p>
        </motion.section>

        {/* Categorías */}
        <section className="mt-12">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles size={20} className="text-brand-magenta" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-brand-ink">¡Participa! Estas son las categorías</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {CATS.map((c) => (
              <motion.div key={c.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="border border-[#E5E5E5] rounded-2xl p-5 text-center">
                <span className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-3" style={{ background: c.c }}>
                  <c.icon size={22} className="text-white" />
                </span>
                <p className="font-semibold text-brand-ink text-sm">{c.t}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Video + Bases */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="flex items-center justify-center">
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] bg-black w-full max-w-[300px] aspect-[9/16]">
              <video src={VIDEO_URL} controls playsInline preload="metadata" className="w-full h-full object-cover" data-testid="concurso-video" />
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="rounded-2xl border border-[#E5E5E5] p-6 flex flex-col sm:flex-row gap-5 items-center">
            <a href={PDF} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
              <img src="/concurso/convocatoria-portada.png" alt="Portada de la convocatoria" className="w-40 rounded-lg border border-[#E5E5E5] shadow-sm" />
            </a>
            <div>
              <h3 className="font-heading text-xl font-bold tracking-tight text-brand-ink">Consulta las bases completas</h3>
              <p className="mt-2 text-sm text-brand-ink/70">Descarga la convocatoria oficial con todos los requisitos, fechas y premios.</p>
              <a href={PDF} download data-testid="bases-download" className="mt-4 inline-flex items-center gap-2 bg-brand-ink text-white font-semibold px-5 py-3 rounded-full hover:bg-brand-magenta transition-colors">
                Descargar bases <FileDown size={16} />
              </a>
            </div>
          </motion.div>
        </section>

        {/* Registro embebido */}
        <section className="mt-14" data-testid="registro-section">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink mb-2">Regístrate aquí</h2>
          <p className="text-brand-ink/70 mb-6">Completa el formulario para participar en el concurso.</p>

          {/* Lenguas indígenas */}
          <div className="rounded-2xl border border-[#E5E5E5] p-6 mb-8" style={{ background: "#FFF3E0" }} data-testid="lenguas-indigenas">
            <div className="flex items-center gap-2">
              <Languages size={20} className="text-brand-orange" />
              <h3 className="font-heading text-lg font-bold text-brand-ink">Convocatoria en lenguas indígenas</h3>
            </div>
            <p className="text-sm text-brand-ink/70 mt-2">
              También puedes registrarte con la convocatoria traducida a tu lengua:
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {LENGUAS.map((l) => (
                l.url ? (
                  <a
                    key={l.nombre}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lengua-${l.nombre.toLowerCase()}`}
                    className="inline-flex items-center gap-2 bg-white border border-[#E5E5E5] text-brand-ink font-semibold px-4 py-2.5 rounded-full hover:bg-brand-ink hover:text-white transition-colors"
                  >
                    {l.nombre} <ExternalLink size={14} />
                  </a>
                ) : (
                  <span key={l.nombre} className="inline-flex items-center gap-2 bg-white/60 border border-dashed border-[#D8D8D8] text-brand-ink/50 font-semibold px-4 py-2.5 rounded-full">
                    {l.nombre} · próximamente
                  </span>
                )
              ))}
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] bg-white">
            <iframe src={FORM_URL} title="Formulario de registro" data-testid="registro-form" className="w-full" style={{ height: "560px", border: "none" }} loading="lazy">
              Cargando…
            </iframe>
          </div>
        </section>
      </div>

      {/* Talleres */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-4" data-testid="talleres-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={20} className="text-brand-magenta" />
          <span className="text-xs font-bold uppercase tracking-widest text-brand-ink/50">Talleres</span>
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">Estaremos dando talleres</h2>
        <p className="mt-4 text-brand-ink/80 leading-relaxed max-w-3xl">
          Como parte de la 5.ª edición del Concurso Nacional Tejiendo Redes Ciudadanas por Juventudes
          Diversas, comenzamos con nuestro primer taller virtual para acompañarte en el proceso creativo.
        </p>

        {/* Registro universal para todos los talleres */}
        <div className="mt-6 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" style={{ background: "#FDE7FA" }} data-testid="talleres-registro-universal">
          <div>
            <p className="font-heading text-lg font-bold text-brand-ink">Regístrate a los talleres</p>
            <p className="mt-1 text-sm text-brand-ink/70">Un solo registro para los 5 talleres. Los próximos talleres se anunciarán próximamente.</p>
          </div>
          <a
            href="https://forms.gle/dgQYFn9sRHojt8Rf8"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="talleres-registro"
            className="inline-flex items-center gap-2 bg-brand-ink text-white font-semibold px-5 py-3 rounded-full hover:bg-brand-magenta transition-colors whitespace-nowrap flex-shrink-0"
          >
            Registrarme <ExternalLink size={16} />
          </a>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-[#E5E5E5]">
          <img src="/concurso/taller-a.jpeg" alt="De juventudes para juventudes — Encuentro de saberes y diversidades" className="w-full h-auto" />
          <img src="/concurso/taller-b.jpeg" alt="Taller 1: Narrar los derechos — cuento corto y participación política juvenil" className="w-full h-auto" />
        </div>

        {/* Próximos talleres (gestionables desde el panel) */}
        {talleres.length > 0 && (
          <div className="mt-8 space-y-8" data-testid="talleres-dinamicos">
            {talleres.map((t) => (
              <motion.div
                key={t.id}
                data-testid={`taller-item-${t.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl overflow-hidden border border-[#E5E5E5]"
              >
                {t.image_ref && (
                  <img src={t.image_ref.startsWith("/") ? t.image_ref : `${API}/media/${t.image_ref}`} alt={t.title} className="w-full h-auto" />
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${t.status === "finalizado" ? "bg-[#E6E6E6] text-brand-ink/60" : "bg-brand-lime text-brand-ink"}`}>
                      {t.status === "finalizado" ? "Finalizado" : "Próximo"}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-brand-ink">{t.title}</h3>
                  {t.description && (
                    <div className="mt-2 space-y-1.5 text-brand-ink/80">
                      {t.description.split("\n").filter(Boolean).map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  )}
                  {t.recording_url && (
                    <a
                      href={t.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`taller-grabacion-${t.id}`}
                      className="mt-4 inline-flex items-center gap-2 bg-brand-magenta text-white font-semibold px-5 py-3 rounded-full hover:bg-brand-orange transition-colors"
                    >
                      <PlayCircle size={18} /> Ver grabación del taller
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Banner 10 */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <img src="/concurso/banner-10.webp" alt="Participa — categorías del concurso" className="w-full h-auto rounded-2xl border border-[#E5E5E5]" />
      </section>
    </div>
  );
};

export default TejiendoRedes;
